import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  IPlatformToolComponent,
  IPlatformToolHandler
} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import { InvalidateSubject } from '@shared/helpers/invalidate.subject';
import { MagicLinkModel, TabModel } from '@shared/models/platform-tool-models';
import { PlatformCommand, PlatformCommandResponse } from '@shared/commands/command-base';
import { PlatformQuery } from '@shared/models/platform-query/platform-query';
import { PlatformQueryService } from '@core/services/platform-query.service';
import { CommandService } from '@core/services/command.service';
import { Api } from '@shared/constants/common-field.constants';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

// One flattened (path, displayValue) pair rendered as a row inside a form card.
interface FieldEntry {
  key: string;     // e.g. "CPACertification.Title" or "Items[2]"
  value: string;   // pre-stringified for display: "true" / "false" / "—" / native string
}

// One form's card-ready data.
interface FormView {
  schemaId: string;
  title: string;            // "CA-GMT Version 1" — same formula buildTitle uses in the Preview
  country: string;
  entries: FieldEntry[];    // sorted with natural-numeric ordering so Items[2] precedes Items[10]
  searchHaystack: string;   // lowercased "title key1 value1 key2 value2 …" for the form-name filter
}

@Component({
  selector: 'app-form-config-fieldvalues-inspector',
  standalone: false,
  templateUrl: './form-config-fieldvalues-inspector.component.html',
  styleUrl: './form-config-fieldvalues-inspector.component.scss'
})
export class FormConfigFieldValuesInspectorComponent
  implements OnInit, OnDestroy, IPlatformToolComponent {
  handler: IPlatformToolHandler;
  invalidateSubject: InvalidateSubject;
  magicLinkModel: MagicLinkModel;
  selectedTab: TabModel;

  isLoading = false;
  errorMessage: string | null = null;
  allForms: FormView[] = [];
  viewForms: FormView[] = [];

  formNameFilter = '';
  keyFilter = '';

  private formNameFilter$ = new BehaviorSubject<string>('');
  private keyFilter$ = new BehaviorSubject<string>('');
  private unSubscribe$ = new Subject<void>();

  constructor(private platformQueryService: PlatformQueryService,
              private commandService: CommandService) {}

  ngOnInit(): void {
    this.formNameFilter$
      .pipe(debounceTime(200), takeUntil(this.unSubscribe$))
      .subscribe(v => { this.formNameFilter = v; this.applyFilters(); });
    this.keyFilter$
      .pipe(debounceTime(200), takeUntil(this.unSubscribe$))
      .subscribe(v => { this.keyFilter = v; this.applyFilters(); });

    // Re-fetch on any platform-driven tool-body invalidate (e.g., after the
    // Edit Metadata dialog save). The component instance is preserved across
    // RefreshPlatformLink, so ngOnInit doesn't re-run on its own — without
    // this subscription the inspector would show stale data until manual reload.
    if (this.invalidateSubject) {
      this.invalidateSubject.getObservale()
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe(() => this.fetchAll());
    }

    this.fetchAll();
  }

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  onAfterExecuteNextCommand(_: PlatformCommandResponse) {}
  onAfterExecutePreviousCommand(_: PlatformCommandResponse) {}
  onBeforeExecuteNextCommand(): boolean { return false; }
  onBeforeExecutePreviousCommand(): boolean { return false; }
  onLoad() {}

  trackByForm = (_: number, f: FormView) => f.schemaId;
  trackByEntry = (_: number, e: FieldEntry) => e.key;

  onFormNameFilterChange(v: string): void { this.formNameFilter$.next(v ?? ''); }
  onKeyFilterChange(v: string): void { this.keyFilter$.next(v ?? ''); }

  // Dispatch the existing EditFormMetadataCommand for this form's schemaId.
  // The consumer round-trips twice on the same observable:
  //   1) first response carries PlatformDialogId — the platform notification
  //      handler opens the Edit Metadata dialog.
  //   2) second response (after dialog Save) carries RefreshPlatformLink=true
  //      + a SnackBarMessage.
  // We subscribe without take(1) so we see both, and call fetchAll() when the
  // save response arrives. takeUntil(unSubscribe$) cleans up on destroy.
  onEditCard(form: FormView): void {
    if (!form?.schemaId) return;
    const command = new PlatformCommand('EditFormMetadataCommand', Api.FilingManagerApi);
    command.fieldValues['ItemId'] = form.schemaId;
    this.commandService.executePlatformCommand(command)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((response: PlatformCommandResponse) => {
        if (response?.fieldValues?.['RefreshPlatformLink']) {
          this.fetchAll();
        }
      });
  }

  // Reuses the same query the Import Preview hits — no IncludeMetaDataChangeModel
  // because we only need the live config side, not the staged diff.
  private fetchAll(): void {
    const query = new PlatformQuery();
    query.name = 'FormConfigModelByIdQuery';
    query.api  = Api.FilingManagerApi;
    query.fieldValues['IsLive'] = true;

    this.isLoading = true;
    this.errorMessage = null;

    this.platformQueryService.executeQuery(query)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (r) => {
          this.isLoading = false;
          const configs: any[] = r?.fieldValues?.['FormConfigModels'] ?? [];
          this.allForms = this.buildFormViews(configs);
          this.applyFilters();
        },
        error: (err) => {
          this.isLoading = false;
          this.errorMessage = err?.message ?? 'Failed to load form metadata.';
        }
      });
  }

  private buildFormViews(configs: any[]): FormView[] {
    const views: FormView[] = [];
    for (const c of configs) {
      const country  = c?.countryCode ?? c?.CountryCode ?? '';
      const formType = c?.formType    ?? c?.FormType    ?? '';
      const tool     = c?.toolName    ?? c?.ToolName    ?? '';
      const version  = c?.version     ?? c?.Version     ?? '';
      const head  = [country, formType, tool].filter(Boolean).join('-');
      const title = version ? `${head} Version ${version}` : (head || '(unknown schema)');

      const fieldValues = c?.fieldValues ?? c?.FieldValues ?? {};
      const flatPairs   = this.flatten(fieldValues);
      flatPairs.sort((a, b) => this.naturalCompare(a.key, b.key));

      const entries: FieldEntry[] = flatPairs.map(p => ({
        key: p.key,
        value: this.stringifyLeaf(p.value)
      }));

      const haystack = (title + ' ' + entries.map(e => e.key + ' ' + e.value).join(' ')).toLowerCase();

      views.push({
        schemaId: c?.id ?? c?.Id ?? '',
        title,
        country,
        entries,
        searchHaystack: haystack
      });
    }
    views.sort((a, b) => a.title.localeCompare(b.title));
    return views;
  }

  // - name filter: substring on the card's haystack (title + every key + every value)
  // - key  filter: drops *individual* entries whose path doesn't match; cards left
  //                with zero entries are hidden ONLY when the key filter is active
  //                (so an inherently-empty form still renders as "(empty)" when no
  //                key filter is set).
  private applyFilters(): void {
    const name = this.formNameFilter.trim().toLowerCase();
    const key  = this.keyFilter.trim().toLowerCase();

    this.viewForms = this.allForms
      .map(f => key
        ? { ...f, entries: f.entries.filter(e => e.key.toLowerCase().includes(key)) }
        : f)
      .filter(f => !name || f.searchHaystack.includes(name))
      .filter(f => key ? f.entries.length > 0 : true);
  }

  // Dot/bracket grammar identical to the backend FormMetadataPathHelper.Flatten and
  // the Import Preview's flattenInto, so paths displayed here line up character-for-
  // character with paths displayed in those other views.
  private flatten(value: any, prefix = ''): Array<{ key: string; value: any }> {
    const out: Array<{ key: string; value: any }> = [];
    if (value === null || value === undefined) {
      out.push({ key: prefix, value: null });
      return out;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) { out.push({ key: prefix, value: [] }); return out; }
      value.forEach((v, i) => out.push(...this.flatten(v, `${prefix}[${i}]`)));
      return out;
    }
    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) { out.push({ key: prefix, value: {} }); return out; }
      for (const k of keys) {
        const childKey = prefix === '' ? k : `${prefix}.${k}`;
        out.push(...this.flatten(value[k], childKey));
      }
      return out;
    }
    out.push({ key: prefix, value });
    return out;
  }

  private stringifyLeaf(v: any): string {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'boolean') return v ? 'true' : 'false';
    if (Array.isArray(v) && v.length === 0) return '[ ]';
    if (typeof v === 'object' && Object.keys(v).length === 0) return '{ }';
    return String(v);
  }

  // Natural-numeric ordering so Items[2] sorts before Items[10] instead of after.
  // Same comparator the Preview's PathNaturalComparer implements server-side.
  private naturalCompare(a: string, b: string): number {
    let ai = 0, bi = 0;
    while (ai < a.length && bi < b.length) {
      const ac = a[ai], bc = b[bi];
      if (/[0-9]/.test(ac) && /[0-9]/.test(bc)) {
        let an = 0, bn = 0;
        while (ai < a.length && /[0-9]/.test(a[ai])) { an = an * 10 + +a[ai]; ai++; }
        while (bi < b.length && /[0-9]/.test(b[bi])) { bn = bn * 10 + +b[bi]; bi++; }
        if (an !== bn) return an - bn;
        continue;
      }
      const cmp = ac.toLowerCase().localeCompare(bc.toLowerCase());
      if (cmp !== 0) return cmp;
      ai++; bi++;
    }
    return a.length - b.length;
  }
}
